/**
 * @genesis_domain Settings — Preferences Mutation Hook
 * @genesis_version V9.1
 * @genesis_intent TanStack Query mutation hook for updating the authenticated
 *   user's preferences. Implements the three-phase optimistic update pattern:
 *   onMutate snapshot → onError rollback → onSettled invalidation.
 * @genesis_boundaries FORBIDDEN from containing raw fetch() calls or UI logic.
 *   FORBIDDEN from using Logical OR (||) for fallbacks — Nullish Coalescing (??) only.
 * @genesis_ai_trail Phase 3 Hybrid Sync Preferences. Follows use[Entity]Mutation
 *   naming registry. Three-phase optimistic update ensures instant UI feedback
 *   with guaranteed server sync on error recovery.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { preferenceKeys } from '@/core/api/queryKeys';
import { updatePreferences } from '@/domains/settings/services/preferencesApiService';
import type { UpdatePreferences, UserProfile } from '@shared/schemas';
import { deepMergePreferences } from '@shared/utils/preferenceMerge';

interface OptimisticContext {
    previousProfile: UserProfile | undefined;
}

/**
 * Mutation hook for updating user preferences.
 *
 * Three-Phase Optimistic Update (Phase 75E State Integrity Strike):
 * 1. onMutate: Cancel in-flight preference queries, snapshot previous cache
 *    state, optimistically deep-merge the cache with the new preferences using
 *    the shared deepMergePreferences utility — preserves sibling keys inside
 *    nested export, appearance, and measurements sub-objects.
 * 2. onError: Rollback the cache to the previous snapshot on failure.
 * 3. onSettled: Invalidate preference queries to refetch from server.
 */
export function usePreferencesMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (payload: UpdatePreferences) => updatePreferences(payload),
        onMutate: async (payload): Promise<OptimisticContext> => {
            // Phase 1: Cancel in-flight queries and snapshot current cache
            await queryClient.cancelQueries({ queryKey: preferenceKeys.all });

            const previousProfile = queryClient.getQueryData<UserProfile>(
                preferenceKeys.profile(),
            );

            // Optimistically deep-merge the cache with the partial payload
            if (previousProfile) {
                queryClient.setQueryData<UserProfile>(
                    preferenceKeys.profile(),
                    {
                        ...previousProfile,
                        preferences: deepMergePreferences(
                            previousProfile.preferences,
                            payload.preferences,
                        ),
                    },
                );
            }

            return { previousProfile };
        },
        onError: (_error, _payload, context) => {
            // Phase 2: Rollback to snapshot on failure
            if (context?.previousProfile) {
                queryClient.setQueryData(
                    preferenceKeys.profile(),
                    context.previousProfile,
                );
            }
        },
        onSettled: () => {
            // Phase 3: Invalidate queries to refetch from server
            queryClient.invalidateQueries({ queryKey: preferenceKeys.all });
        },
    });
}
